import React from "react";
import styled from "styled-components";
import { FiBookOpen, FiBriefcase } from "react-icons/fi";
import { formatTimelineDate } from "./timelineEntries";

const Timepoint = ({ timepoint, last }) => {
  const { id, date_from, date_to, position, company, present, category, summary } = timepoint;
  const Icon = category === "Education" ? FiBookOpen : FiBriefcase;

  return (
    <Entry>
      <Dates><time dateTime={date_from}>{formatTimelineDate(date_from)}</time><span aria-hidden="true"> — </span>{present ? <span>Present</span> : <time dateTime={date_to}>{formatTimelineDate(date_to)}</time>}</Dates>
      <Track $last={last} aria-hidden="true"><Dot $current={present} /></Track>
      <Card aria-labelledby={`role-${id}`}>
        <CardHeader>
          <Type><Icon aria-hidden="true" />{category}</Type>
          {present && <Current><span aria-hidden="true" /> Current</Current>}
        </CardHeader>
        <h3 id={`role-${id}`}>{position}</h3>
        <Company>{company}</Company>
        <Summary>{summary}</Summary>
      </Card>
    </Entry>
  );
};

export default Timepoint;

const Entry = styled.li`
  display: grid;
  grid-template-columns: 1rem minmax(0, 1fr);
  gap: 0.6rem 0.75rem;
  position: relative;
  padding-bottom: 1.75rem;
  &:last-child { padding-bottom: 0; }
  @media (min-width: 1200px) {
    grid-template-columns: 6.5rem 1rem minmax(0, 1fr);
    column-gap: 1rem;
  }
`;
const Dates = styled.div`
  grid-column: 2;
  grid-row: 1;
  color: #65736b;
  font-size: 0.7rem;
  line-height: 1.6;
  @media (min-width: 1200px) {
    grid-column: 1;
    padding-top: 1.4rem;
    time, span { display: block; }
    span[aria-hidden] { display: none; }
  }
`;
const Track = styled.span`
  position: relative;
  grid-column: 1;
  grid-row: 1 / span 2;
  display: flex;
  justify-content: center;
  padding-top: 0.25rem;
  &::before {
    content: "";
    display: ${({ $last }) => $last ? "none" : "block"};
    position: absolute;
    width: 1px;
    top: 0.5rem;
    bottom: -1.75rem;
    background: #cbded1;
  }
  @media (min-width: 1200px) {
    grid-column: 2;
    grid-row: 1;
    padding-top: 1.6rem;
    &::before { top: 1.75rem; bottom: -3.35rem; }
  }
`;
const Dot = styled.span`
  position: relative;
  width: 9px;
  height: 9px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 2px solid ${({ $current }) => $current ? "#008369" : "#a5bfae"};
  background: ${({ $current }) => $current ? "#008369" : "#fcfdfc"};
  box-shadow: 0 0 0 4px #fcfdfc;
`;
const Card = styled.article`
  --timeline-muted: #596b60;
  --timeline-tag-border: #dfe9e2;
  --timeline-accent: #007b61;
  grid-column: 2;
  grid-row: 2;
  min-width: 0;
  padding: 1.25rem;
  border: 1px solid #dce7df;
  border-radius: 1.1rem;
  color: #253d31;
  background: #fff;
  box-shadow: 0 6px 20px #183c3205;
  h3 { margin-top: 1rem; font-size: clamp(1rem, 1.6vw, 1.2rem); font-weight: 600; line-height: 1.4; letter-spacing: -0.025em; overflow-wrap: anywhere; }
  @media (min-width: 600px) { padding: 1.5rem; }
  @media (min-width: 1200px) { grid-column: 3; grid-row: 1; }
`;
const CardHeader = styled.div`display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 0.5rem;`;
const Type = styled.span`display: inline-flex; align-items: center; gap: 0.45rem; color: var(--timeline-muted); font-size: 0.65rem; letter-spacing: 0.06em; text-transform: uppercase; svg { font-size: 0.95rem; }`;
const Current = styled.span`
  display: inline-flex; align-items: center; gap: 0.4rem; padding: 0.3rem 0.6rem;
  border: 1px solid var(--timeline-tag-border); border-radius: 999px; color: var(--timeline-accent); font-size: 0.65rem;
  span { width: 5px; height: 5px; border-radius: 50%; background: currentColor; }
`;
const Company = styled.p`margin-top: 0.35rem; color: var(--timeline-accent); font-size: 0.85rem; font-weight: 500; line-height: 1.6;`;
const Summary = styled.p`margin-top: 1rem; color: var(--timeline-muted); font-size: 0.8rem; line-height: 1.85;`;
